<template>
  <b-card
    v-if="canRead && customerToken"
    class="mt-1 meufuturo-app-access"
  >
    <div class="d-flex justify-content-between align-items-start flex-wrap">
      <h5 class="mb-1">
        <feather-icon
          icon="SmartphoneIcon"
          size="16"
          class="mr-50"
        />
        Acesso Meu Futuro App
      </h5>
      <div class="d-flex align-items-center">
        <b-button
          v-b-tooltip.hover
          variant="flat-secondary"
          size="sm"
          class="btn-icon mr-50"
          title="Atualizar"
          aria-label="Atualizar acesso ao Meu Futuro App"
          :disabled="loading || republishing"
          @click="load"
        >
          <feather-icon icon="RefreshCwIcon" />
        </b-button>
        <span
          v-if="canManage"
          v-b-tooltip.hover
          :title="republishHint"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!canRepublish"
            @click="republish"
          >
            <b-spinner
              v-if="republishing"
              small
              class="mr-50"
            />
            Republicar estado
          </b-button>
        </span>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center py-1"
    >
      <b-spinner
        small
        class="mr-50"
      />
      Carregando...
    </div>

    <b-alert
      v-else-if="errorMessage"
      show
      variant="warning"
      class="mb-0"
    >
      <div class="alert-body">
        {{ errorMessage }}
      </div>
    </b-alert>

    <template v-else-if="access">
      <b-row>
        <b-col
          md="6"
          cols="12"
          class="mb-1"
        >
          <small class="text-muted d-block">Vínculo com o app</small>
          <template v-if="linked">
            <b-badge variant="light-success">
              Vinculado
            </b-badge>
            <small class="d-block text-muted text-break mt-25">
              ID no app: {{ access.meufuturoUserId }}
            </small>
          </template>
          <b-badge
            v-else
            variant="light-secondary"
          >Não vinculado</b-badge>
        </b-col>
        <b-col
          md="6"
          cols="12"
          class="mb-1"
        >
          <small class="text-muted d-block">Método do vínculo</small>
          <span>{{ linkMethod }}</span>
        </b-col>
        <b-col
          md="6"
          cols="12"
          class="mb-1"
        >
          <small class="text-muted d-block">Status da assinatura</small>
          <b-badge :variant="status.variant">
            {{ status.label }}
          </b-badge>
          <small
            v-if="statusDetail"
            class="ml-50 text-muted"
          >{{ statusDetail }}</small>
        </b-col>
        <b-col
          md="6"
          cols="12"
          class="mb-1"
        >
          <small class="text-muted d-block">Publicação para o app</small>
          <b-badge :variant="publication.variant">
            {{ publication.label }}
          </b-badge>
          <small class="d-block text-muted mt-25">
            Versão {{ displayValue(access.version) }}
            · publicada {{ displayValue(access.publishedVersion) }}
            · última publicação {{ lastPublishedAt }}
          </small>
        </b-col>
      </b-row>

      <div class="border-top pt-1">
        <small class="text-muted d-block mb-25">Contrato de origem</small>
        <span
          v-if="!source"
          class="text-muted"
        >Nenhum contrato com direito ao app.</span>
        <div
          v-else
          class="d-flex flex-wrap"
        >
          <span class="mr-2">
            Contrato
            <b-link
              v-if="source.contractId && canReadContract"
              :to="{ name: 'contract-details-summary', params: { id: source.contractId } }"
            >#{{ source.contractId }}</b-link>
            <template v-else>#{{ displayValue(source.contractId) }}</template>
          </span>
          <span class="mr-2">Produto: {{ source.product }}</span>
          <span class="mr-2">Gateway: {{ source.gateway }}</span>
          <span>Ciclo: {{ source.billingCycle }}</span>
        </div>
      </div>
    </template>
  </b-card>
</template>

<script>
import {
  BCard, BBadge, BButton, BAlert, BRow, BCol, BSpinner, BLink, VBTooltip,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  READ_ACTION,
  MANAGE_ACTION,
  CONTRACT_RESOURCE,
  MEUFUTURO_APP_ACCESS_RESOURCE,
} from '@/constants/resources'
import {
  EMPTY,
  statusInfo,
  isLinked,
  linkMethodLabel,
  statusDetail,
  formatDateTime,
  sourceSummary,
  publicationState,
  loadErrorMessage,
  republishErrorMessage,
  republishSuccessMessage,
} from '../utils/meufuturoAppAccess'
import * as types from '../store/types'

export default {
  name: 'MeuFuturoAppAccess',
  components: {
    BCard, BBadge, BButton, BAlert, BRow, BCol, BSpinner, BLink,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    customer: {
      type: Object,
      default: undefined,
    },
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      republishing: false,
      errorMessage: null,
    }
  },
  computed: {
    ...mapGetters({
      accessState: types.MEUFUTURO_APP_ACCESS,
    }),
    customerToken() {
      return this.customer?.token || null
    },
    // CASL: quem tem `manage` também passa em `read`.
    canRead() {
      return this.$can(READ_ACTION, MEUFUTURO_APP_ACCESS_RESOURCE)
    },
    canManage() {
      return this.$can(MANAGE_ACTION, MEUFUTURO_APP_ACCESS_RESOURCE)
    },
    canReadContract() {
      return this.$can(READ_ACTION, CONTRACT_RESOURCE)
    },
    access() {
      const state = this.accessState
      return state && state.customerToken === this.customerToken ? state.data : null
    },
    linked() {
      return isLinked(this.access)
    },
    linkMethod() {
      return linkMethodLabel(this.access)
    },
    status() {
      return statusInfo(this.access?.status)
    },
    statusDetail() {
      return statusDetail(this.access)
    },
    lastPublishedAt() {
      return formatDateTime(this.access?.lastPublishedAt) || 'nunca'
    },
    publication() {
      return publicationState(this.access)
    },
    source() {
      return sourceSummary(this.access)
    },
    // O evento só sai com meufuturoUserId: sem vínculo, republicar não leva nada ao app.
    canRepublish() {
      return !!this.access && this.linked && !this.loading && !this.republishing
    },
    republishHint() {
      if (this.access && !this.linked) return 'Cliente sem vínculo com o app: não há estado para publicar.'
      return 'Recalcula o estado da assinatura e reenvia ao Meu Futuro App.'
    },
  },
  watch: {
    // A ficha reusa a instância ao navegar entre perfis (ver Profile.vue).
    customerToken: {
      handler(token, previous) {
        if (token && token !== previous) this.load()
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      getAccess: types.GET_MEUFUTURO_APP_ACCESS,
      republishAccess: types.REPUBLISH_MEUFUTURO_APP_ACCESS,
    }),
    displayValue(value) {
      return value === null || value === undefined || value === '' ? EMPTY : value
    },
    load() {
      if (!this.canRead || !this.customerToken) return
      const token = this.customerToken
      this.loading = true
      this.errorMessage = null
      this.getAccess(token)
        .catch(error => {
          if (token === this.customerToken) this.errorMessage = loadErrorMessage(error)
        })
        .finally(() => {
          if (token === this.customerToken) this.loading = false
        })
    },
    async republish() {
      if (!this.canRepublish) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'O estado da assinatura deste cliente será recalculado e reenviado ao Meu Futuro App. Use quando o app estiver mostrando um acesso diferente do que aparece aqui. Deseja continuar?',
        {
          title: 'Republicar estado',
          okTitle: 'Republicar',
          cancelTitle: 'Cancelar',
          okVariant: 'primary',
          centered: true,
        },
      )
      if (!confirmed) return

      const token = this.customerToken
      this.republishing = true
      this.republishAccess(token)
        .then(response => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Republicação enfileirada',
              text: republishSuccessMessage(response?.data),
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          if (token === this.customerToken) this.load()
        })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: republishErrorMessage(error),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.republishing = false
        })
    },
  },
}
</script>
